import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Transforms Django media URLs to work with Vite proxy
 * Converts absolute URLs from Django server to relative paths
 * @param url - The original URL from Django API
 * @returns Transformed URL that works with Vite proxy
 */
export function transformMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // Already relative path - nothing to do
  if (url.startsWith('/')) return url;

  // Allow an explicit Vite backend base to be configured via env var
  // Example: VITE_BACKEND_URL="http://localhost:8000"
  const backendBase = (import.meta as any).env?.VITE_BACKEND_URL;
  if (backendBase && typeof backendBase === 'string' && url.startsWith(backendBase)) {
    return url.replace(backendBase, '') || '/';
  }

  try {
    const urlObj = new URL(url);
    const isLocalhost = urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1';

    // If the asset is served from the same origin or from localhost backend,
    // return a relative path so Vite's dev server proxy can serve it.
    if (isLocalhost || urlObj.origin === window.location.origin) {
      const path = urlObj.pathname + urlObj.search;

      // Some Django setups (and default FileField.url) may return paths like
      // /book_covers/filename.jpg or /avatars/..., while Django serves them
      // under MEDIA_URL (/media/...). Map these legacy roots to /media so
      // Vite's /media proxy can pick them up during development.
      if (path.startsWith('/book_covers')) return `/media${path}`;
      if (path.startsWith('/avatars')) return `/media${path}`;
      // If already under /media, return as-is
      if (path.startsWith('/media')) return path;

      return path;
    }

    // Otherwise return the absolute URL (external CDN or third-party host)
    return url;
  } catch {
    // If parsing fails, return original value as a last resort
    return url;
  }
}
